/**
 * <announcement-bar-rotator>
 *
 * A dependency-free announcement rotator.
 *  - fade + slide transition between messages
 *  - auto-rotate (opt-in) that pauses on hover, on focus and when the tab is hidden
 *  - swipe support via Pointer Events (no library)
 *  - dot indicators + optional desktop arrows
 *  - honours `prefers-reduced-motion` by never auto-rotating
 */
if (!customElements.get('announcement-bar-rotator')) {
  class AnnouncementBarRotator extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.leaveTimer = null;
      this.paused = false;
      this.dragging = false;
      this.dragAxis = null;
      this.dragDelta = 0;
      this.suppressClick = false;
      this.transitionDuration = 400;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-rotator-slide]'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('[data-rotator-dot]'));
      this.viewport = this.querySelector('.announcement-rotator__viewport');
      this.prevButton = this.querySelector('[data-rotator-prev]');
      this.nextButton = this.querySelector('[data-rotator-next]');

      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.index = activeIndex > -1 ? activeIndex : 0;

      const speed = parseInt(this.dataset.speed, 10);
      this.speed = (isNaN(speed) ? 5 : Math.min(Math.max(speed, 3), 10)) * 1000;
      this.autoplayEnabled = this.dataset.autoplay === 'true';

      this.reducedMotion =
        typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

      this.onPrev = this.previous.bind(this);
      this.onNext = this.next.bind(this);
      this.onDotClick = this.handleDotClick.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onClickCapture = this.handleClickCapture.bind(this);
      this.onMouseEnter = this.pause.bind(this);
      this.onMouseLeave = this.resume.bind(this);
      this.onFocusIn = this.pause.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onMotionChange = this.handleMotionChange.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.resume.bind(this);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);

      if (this.viewport && window.PointerEvent) {
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.viewport.addEventListener('click', this.onClickCapture, true);
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.reducedMotion) {
        if (typeof this.reducedMotion.addEventListener === 'function') {
          this.reducedMotion.addEventListener('change', this.onMotionChange);
        } else if (typeof this.reducedMotion.addListener === 'function') {
          this.reducedMotion.addListener(this.onMotionChange);
        }
      }

      this.setSlideState();
      this.schedule();
    }

    disconnectedCallback() {
      this.stop();
      window.clearTimeout(this.leaveTimer);

      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));

      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);

      if (this.viewport && window.PointerEvent) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport.removeEventListener('click', this.onClickCapture, true);
      }

      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.reducedMotion) {
        if (typeof this.reducedMotion.removeEventListener === 'function') {
          this.reducedMotion.removeEventListener('change', this.onMotionChange);
        } else if (typeof this.reducedMotion.removeListener === 'function') {
          this.reducedMotion.removeListener(this.onMotionChange);
        }
      }
    }

    /* ----------------------------------------------------------------- */

    get prefersReducedMotion() {
      return Boolean(this.reducedMotion && this.reducedMotion.matches);
    }

    schedule() {
      this.stop();
      if (!this.autoplayEnabled || this.paused || this.prefersReducedMotion) return;
      if (document.hidden) return;
      this.timer = window.setTimeout(() => this.goTo(this.index + 1, 'next'), this.speed);
    }

    stop() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.schedule();
    }

    previous() {
      this.goTo(this.index - 1, 'prev');
    }

    next() {
      this.goTo(this.index + 1, 'next');
    }

    goTo(rawIndex, direction) {
      const total = this.slides.length;
      if (!total) return;

      const target = ((rawIndex % total) + total) % total;
      if (target === this.index) {
        this.schedule();
        return;
      }

      const previousSlide = this.slides[this.index];
      this.dataset.direction = direction === 'prev' ? 'prev' : 'next';
      this.index = target;

      if (previousSlide) {
        previousSlide.classList.add('is-leaving');
        window.clearTimeout(this.leaveTimer);
        this.leaveTimer = window.setTimeout(() => {
          this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
        }, this.transitionDuration);
      }

      this.setSlideState();
      this.schedule();
    }

    setSlideState() {
      this.slides.forEach((slide, i) => {
        const isActive = i === this.index;
        slide.classList.toggle('is-active', isActive);
        if (isActive) {
          slide.classList.remove('is-leaving');
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
        slide.querySelectorAll('a[href]').forEach((link) => {
          if (isActive) {
            link.removeAttribute('tabindex');
          } else {
            link.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === this.index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    /* Handlers --------------------------------------------------------- */

    handleDotClick(event) {
      const index = parseInt(event.currentTarget.dataset.index, 10);
      if (isNaN(index)) return;
      this.goTo(index, index > this.index ? 'next' : 'prev');
    }

    handleFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.resume();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.schedule();
      }
    }

    handleMotionChange() {
      if (this.prefersReducedMotion) {
        this.stop();
      } else {
        this.schedule();
      }
    }

    handleKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    handleBlockSelect(event) {
      const slide = this.slides.find((item) => item.contains(event.target) || item === event.target);
      if (!slide) return;
      this.pause();
      const index = this.slides.indexOf(slide);
      this.goTo(index, index > this.index ? 'next' : 'prev');
      this.stop();
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse') return;
      this.dragging = true;
      this.dragAxis = null;
      this.dragDelta = 0;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.pause();
    }

    handlePointerMove(event) {
      if (!this.dragging) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (!this.dragAxis) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        this.dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (this.dragAxis === 'x') {
          this.classList.add('is-dragging');
          if (this.viewport.setPointerCapture) {
            try {
              this.viewport.setPointerCapture(event.pointerId);
            } catch (e) {
              /* no-op */
            }
          }
        }
      }

      if (this.dragAxis !== 'x') return;

      this.dragDelta = deltaX;
      const activeSlide = this.slides[this.index];
      if (activeSlide && !this.prefersReducedMotion) {
        activeSlide.style.transform = 'translateX(' + deltaX * 0.35 + 'px)';
      }
    }

    handlePointerUp() {
      if (!this.dragging) return;

      this.dragging = false;
      this.classList.remove('is-dragging');

      const activeSlide = this.slides[this.index];
      if (activeSlide) activeSlide.style.transform = '';

      const delta = this.dragDelta;
      this.dragDelta = 0;

      if (this.dragAxis === 'x' && Math.abs(delta) > 40) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 0);
        if (delta < 0) {
          this.next();
        } else {
          this.previous();
        }
      }

      this.dragAxis = null;
      this.resume();
    }

    handleClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  customElements.define('announcement-bar-rotator', AnnouncementBarRotator);
}
